"use client";

import React, { useEffect, useMemo } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import type { UserProfile } from "../../../api/types";
import { useUserProfiles } from "../../userProfiles/context/UserProfilesContext";

const getUserLabel = (user: UserProfile | null) =>
  [user?.first_name, user?.last_name]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ");

const getUserInitials = (user: UserProfile | null) => {
  const first = (user?.first_name ?? "").trim();
  const last = (user?.last_name ?? "").trim();
  const email = (user?.email ?? "").trim();

  const firstInitial = first ? first[0] : "";
  const lastInitial = last ? last[0] : "";
  const fromName = `${firstInitial}${lastInitial}`.toUpperCase();
  if (fromName) return fromName;

  return email ? email[0].toUpperCase() : "?";
};

/** 20px neutral stack avatar; `muted` while loading and for the overflow count. */
const StackAvatar = ({
  image,
  muted = false,
  children
}: {
  image?: string;
  muted?: boolean;
  children: React.ReactNode;
}) => (
  <Avatar size="xs" className="bg-surface ring-cutout ring-surface">
    {image ? <AvatarImage src={image} alt="" /> : null}
    <AvatarFallback
      className={cn(
        "bg-fill-2 text-2xs leading-none font-medium",
        muted ? "text-fg-4" : "text-fg-2"
      )}
    >
      {children}
    </AvatarFallback>
  </Avatar>
);

export const AssignedUsersAvatarStack = ({
  userProfiles,
  userIds,
  maxVisible,
  nonInteractive = false
}: {
  userProfiles?: Array<UserProfile | null>;
  userIds?: string[];
  maxVisible?: number;
  /**
   * When true, renders plain (non-button) avatars without per-avatar tooltips.
   * Use this when the stack is nested inside another interactive trigger
   * (e.g. a dropdown/tooltip trigger) that supplies its own affordances.
   */
  nonInteractive?: boolean;
}) => {
  const { profilesById, ensure } = useUserProfiles();
  const normalizedProfiles = useMemo(
    () => (Array.isArray(userProfiles) ? userProfiles : []),
    [userProfiles]
  );

  const normalizedUserIds = useMemo(
    () =>
      Array.isArray(userIds)
        ? userIds.map((id) => id.trim()).filter(Boolean)
        : [],
    [userIds]
  );

  const shouldRenderProfiles = normalizedProfiles.length > 0;

  useEffect(() => {
    if (shouldRenderProfiles) return;
    if (normalizedUserIds.length === 0) return;
    ensure(normalizedUserIds);
  }, [ensure, normalizedUserIds, shouldRenderProfiles]);

  const resolvedProfiles: Array<UserProfile | null> = shouldRenderProfiles
    ? normalizedProfiles
    : normalizedUserIds.length === 0
    ? []
    : normalizedUserIds.map((uid) => profilesById[uid] ?? { uid });

  if (resolvedProfiles.length === 0) return null;

  const normalizedMaxVisible =
    typeof maxVisible === "number" && Number.isFinite(maxVisible)
      ? Math.max(0, Math.floor(maxVisible))
      : undefined;

  const visibleProfiles =
    typeof normalizedMaxVisible === "number" &&
    normalizedMaxVisible > 0 &&
    resolvedProfiles.length > normalizedMaxVisible
      ? resolvedProfiles.slice(0, Math.max(1, normalizedMaxVisible - 1))
      : resolvedProfiles;

  const overflowCount =
    typeof normalizedMaxVisible === "number" && normalizedMaxVisible > 0
      ? Math.max(0, resolvedProfiles.length - visibleProfiles.length)
      : 0;

  const stack = (
    <div className="group flex items-center">
      {visibleProfiles.map((user, idx) => {
        const uid = (user?.uid ?? "").trim() || `unknown-${idx}`;
        const name = getUserLabel(user) || (user?.email ?? "").trim() || "";
        const email = (user?.email ?? "").trim();
        const image = user?.avatar_data_url ?? undefined;
        const initials = getUserInitials(user);
        const tooltipLabel =
          name || email ? (email ? `${name}\n${email}` : name) : "Loading…";
        const ariaLabel =
          name || email ? (email ? `${name} • ${email}` : name) : uid;
        const avatar = (
          <StackAvatar image={image} muted={!(name || email)}>
            {initials}
          </StackAvatar>
        );

        return (
          <div
            key={uid}
            className={[
              "transition-all duration-200 ease-out",
              idx === 0 ? "ml-0" : "-ml-2 group-hover:ml-1"
            ].join(" ")}
            style={{ zIndex: idx + 1 }}
          >
            {nonInteractive ? (
              <span
                className="flex items-center rounded-xs"
                aria-label={ariaLabel}
              >
                {avatar}
              </span>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center rounded-xs"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={ariaLabel}
                  >
                    {avatar}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="whitespace-pre-line">
                  {tooltipLabel}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        );
      })}
      {overflowCount > 0 ? (
        <div
          key="overflow"
          className={[
            "transition-all duration-200 ease-out",
            visibleProfiles.length === 0 ? "ml-0" : "-ml-2 group-hover:ml-1"
          ].join(" ")}
          style={{ zIndex: visibleProfiles.length + 1 }}
        >
          {nonInteractive ? (
            <span
              className="flex items-center rounded-xs"
              aria-label={`${overflowCount} more assignees`}
            >
              <StackAvatar muted>+{overflowCount}</StackAvatar>
            </span>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="flex items-center rounded-xs"
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`${overflowCount} more assignees`}
                >
                  <StackAvatar muted>+{overflowCount}</StackAvatar>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">{`${overflowCount} more`}</TooltipContent>
            </Tooltip>
          )}
        </div>
      ) : null}
    </div>
  );

  if (nonInteractive) return stack;

  return <TooltipProvider delayDuration={150}>{stack}</TooltipProvider>;
};
